// Libraries
import { Fragment } from 'react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';
// Components
import { RatingsIcon } from '@atoms/index';
// Utils
import { formatDateToUtc, formatNumberToDecimal } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { CiiRatingTooltipProps } from './CiiRatingTooltip.types';

/**
 * CiiRatingTooltip component to display a tooltip with CII rating information.
 *
 * @component
 * @param {CiiRatingTooltipProps} props - The component props.
 * @param {RatingKeys} props.ciiRating - The CII rating.
 * @param {number} props.attainedScore - The attained CII score.
 * @param {RatingKeys} props.requiredScore - The required CII score.
 * @param {string} props.lastNoonReportDateUTC - The last noon report date.
 * @returns {ReactElement} - The JSX element representing the CiiRatingTooltip component.
 */
export const CiiRatingTooltip = ({
  firstLabel,
  secondLabel,
  score,
  ciiRating,
  requiredScore,
  lastNoonReportDateUTC,
}: CiiRatingTooltipProps) => {
  const label = (
    <Fragment>
      <Text noOfLines={1}>{`${firstLabel}: ${score ? formatNumberToDecimal(score, 2) : text.na}`}</Text>
      {requiredScore && (
        <Text noOfLines={1} variant='faded'>{`${secondLabel}: ${formatNumberToDecimal(requiredScore, 2)}`}</Text>
      )}
      {lastNoonReportDateUTC && (
        <Text noOfLines={1} variant='faded'>{`${text.fleet_rating_table.columns.last_noon_report}: ${formatDateToUtc(
          lastNoonReportDateUTC,
          'MM/dd/yy'
        )}`}</Text>
      )}
    </Fragment>
  );
  return (
    <Tooltip hasArrow padding={2} label={label} bg='white' placement='top' openDelay={100} closeDelay={200} gutter={12}>
      <Flex alignItems='center' flexDirection='column'>
        {ciiRating ? <RatingsIcon w={5} h={5} rating={ciiRating} dark /> : <Text fontSize='s'>{text.na}</Text>}
      </Flex>
    </Tooltip>
  );
};
